import React, { useState, useRef, useCallback, useEffect } from 'react';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { getFirmwareChannel, getAvailableFirmwareChannels, getSeedableChannels, seedFirmware } from '../services/storageService';
import {
  VARIANTS,
  VARIANT_LABELS,
  SERIAL_PORT_FILTERS,
  connectAndDetect,
  closeTransport,
  loadFirmwareImages,
  flashImages,
  rebootIntoApp,
  formatVersion,
  formatChannelName,
  formatFileSize,
  compareManifestVersions
} from '../services/espFlasher';
import { 
  trackOTAPageView, 
  trackOTAChannelSelected, 
  trackOTAFlashStarted, 
  trackOTAFlashCompleted, 
  trackOTAFlashFailed,
  trackDeviceConnected,
  trackDeviceDisconnected,
  trackDeviceUnsupported
} from '../services/analytics';
import './OTAManagementPage.css';

// Variants a @rekkie.com user can seed firmware for.
const SEEDABLE_VARIANTS = VARIANTS;

const OTAManagementPage = () => {
  const { currentUser } = useAuth();
  const { addError } = useError();

  // Browser support check
  const [isSupported] = useState('serial' in navigator);

  // Check if user is a @rekkie.com user
  const isRekkieUser = currentUser?.email?.toLowerCase().endsWith('@rekkie.com') || false;
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chipInfo, setChipInfo] = useState(null);
  
  // Flashing state
  const [isFlashing, setIsFlashing] = useState(false);
  const [flashProgress, setFlashProgress] = useState(0);
  const [flashStatus, setFlashStatus] = useState('');
  const [flashLogs, setFlashLogs] = useState([]);
  const [flashComplete, setFlashComplete] = useState(false);
  // False when the post-flash restart signal could not be sent, so the
  // completion message falls back to the manual unplug/reconnect steps.
  const [rebootSent, setRebootSent] = useState(true);
  
  // Firmware selection state
  const [availableChannels, setAvailableChannels] = useState([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [manifests, setManifests] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [isLoadingManifests, setIsLoadingManifests] = useState(false);
  const [showOtherVersions, setShowOtherVersions] = useState(false);

  // Seed firmware state (only for @rekkie.com users)
  const [showSeedForm, setShowSeedForm] = useState(false);
  const [seedableChannels, setSeedableChannels] = useState([]);
  const [seedChannel, setSeedChannel] = useState('');
  const [seedBinFile, setSeedBinFile] = useState(null);
  const [seedElfFile, setSeedElfFile] = useState(null);
  const [seedMajor, setSeedMajor] = useState('');
  const [seedMinor, setSeedMinor] = useState('');
  const [seedBuild, setSeedBuild] = useState('');
  const [seedPatch, setSeedPatch] = useState('');
  const [seedChangelog, setSeedChangelog] = useState('');
  const [seedDevNotes, setSeedDevNotes] = useState('');
  const [seedVariant, setSeedVariant] = useState(SEEDABLE_VARIANTS[0]);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  // Refs
  const portRef = useRef(null);
  const transportRef = useRef(null);
  const espLoaderRef = useRef(null);
  const logsEndRef = useRef(null);

  // Firmware variant of the connected device (null if nothing connected or the
  // chip isn't a supported REKKIE goggle). Drives which channels/firmware load.
  const variant = chipInfo ? chipInfo.variant : null;
  const isSupportedDevice = !!variant;

  // Cleanup on unmount - close port properly
  useEffect(() => {
    return () => {
      if (transportRef.current) {
        closeTransport(transportRef.current);
      } else if (portRef.current) {
        portRef.current.close().catch(() => {});
      }
      portRef.current = null;
      transportRef.current = null;
      espLoaderRef.current = null;
    };
  }, []);

  // Log page access on mount
  useEffect(() => {
    trackOTAPageView();
  }, []);

  // Load available channels once a supported device is connected. Channels
  // themselves are variant-independent (they're gated by user email + Remote
  // Config), but we only surface them after we know which goggle is plugged in
  // so the firmware we then fetch matches the connected hardware variant.
  useEffect(() => {
    if (!variant) {
      // Nothing supported connected — clear any prior firmware selection.
      setAvailableChannels([]);
      setSelectedChannel('');
      setManifests([]);
      setSelectedManifest(null);
      setShowOtherVersions(false);
      return;
    }
    let cancelled = false;
    const loadChannels = async () => {
      setIsLoadingChannels(true);
      try {
        const data = await getAvailableFirmwareChannels();
        if (cancelled) return;
        if (data && data.channels) {
          setAvailableChannels(data.channels);
        } else {
          addError('Failed to load firmware channels', 'No channel data returned from server', 'API');
        }
      } catch (error) {
        if (!cancelled) addError('Failed to load firmware channels', error.message, 'API');
      } finally {
        if (!cancelled) setIsLoadingChannels(false);
      }
    };
    loadChannels();
    return () => { cancelled = true; };
  }, [variant, addError]);

  // Load seedable channels for @rekkie.com users
  useEffect(() => {
    if (!isRekkieUser) return;
    const loadSeedableChannels = async () => {
      const data = await getSeedableChannels();
      if (data && data.channels) {
        setSeedableChannels(data.channels);
      }
    };
    loadSeedableChannels();
  }, [isRekkieUser]);

  // Handle firmware seeding
  const handleSeedFirmware = async () => {
    if (isSeeding) return;

    // Validate all fields
    if (!seedBinFile || !seedElfFile || !seedChannel || !seedChangelog.trim() || !seedDevNotes.trim()) {
      addError('All fields are required', 'Please fill in all fields and select both files', 'Seed');
      return;
    }

    const major = parseInt(seedMajor, 10);
    const minor = parseInt(seedMinor, 10);
    const build = parseInt(seedBuild, 10);
    const patch = parseInt(seedPatch, 10);
    if ([major, minor, build, patch].some(v => isNaN(v) || v < 0)) {
      addError('Invalid build number', 'All version fields must be non-negative integers', 'Seed');
      return;
    }

    setIsSeeding(true);
    setSeedResult(null);
    try {
      const result = await seedFirmware({
        firmwareBin: seedBinFile,
        firmwareElf: seedElfFile,
        major, minor, build, patch,
        channel: seedChannel,
        changelog: seedChangelog,
        developerNotes: seedDevNotes,
        variant: seedVariant
      });
      setSeedResult({ success: true, version: result.version, channel: result.channel });
      // Reset form
      setSeedBinFile(null);
      setSeedElfFile(null);
      setSeedMajor('');
      setSeedMinor('');
      setSeedBuild('');
      setSeedPatch('');
      setSeedChangelog('');
      setSeedDevNotes('');
      setSeedChannel('');
    } catch (error) {
      setSeedResult({ success: false, message: error.message });
      addError('Failed to seed firmware', error.message, 'Seed');
    } finally {
      setIsSeeding(false);
    }
  };

  // Check if seed form is complete
  const isSeedFormValid = seedBinFile && seedElfFile && seedChannel &&
    seedChangelog.trim() && seedDevNotes.trim() &&
    seedMajor !== '' && seedMinor !== '' && seedBuild !== '' && seedPatch !== '' &&
    ![parseInt(seedMajor), parseInt(seedMinor), parseInt(seedBuild), parseInt(seedPatch)].some(v => isNaN(v) || v < 0);

  // Add log entry
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
    
    setFlashLogs(prev => [...prev.slice(-500), { timestamp, message, type, id: Date.now() + Math.random() }]);
    
    // Auto-scroll the log box only, so the page itself stays where the user left it.
    setTimeout(() => {
      const container = logsEndRef.current?.parentElement;
      if (container) container.scrollTop = container.scrollHeight;
    }, 50);
  }, []);

  // Clear logs
  const clearLogs = () => {
    setFlashLogs([]);
  };

  // Export logs
  const exportLogs = () => {
    const logText = flashLogs.map(log => `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`).join('\n');
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ota-flash-log-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle channel selection
  const handleChannelSelect = async (channel) => {
    if (channel === selectedChannel) return;
    
    setSelectedChannel(channel);
    setManifests([]);
    setSelectedManifest(null);
    setShowOtherVersions(false);
    
    if (!channel) return;
    
    // Log channel selection
    trackOTAChannelSelected(channel);
    
    setIsLoadingManifests(true);
    addLog(`Loading ${channel} channel manifests...`, 'info');
    
    try {
      const data = await getFirmwareChannel(channel, variant);
      if (data && data.manifests && data.manifests.length > 0) {
        // Sort manifests by version: major -> minor -> patch -> build (highest first)
        const sortedManifests = [...data.manifests].sort(compareManifestVersions);
        setManifests(sortedManifests);
        // Auto-select the latest (first after sorting) version
        const latestManifest = sortedManifests[0];
        setSelectedManifest(latestManifest);
        const version = formatVersion(latestManifest.manifestFirmware);
        addLog(`Found ${sortedManifests.length} firmware version(s) in ${channel} channel`, 'success');
        addLog(`Auto-selected latest version: ${version}`, 'info');
      } else {
        addLog(`No firmware found in ${channel} channel`, 'warning');
      }
    } catch (error) {
      addError(`Failed to load ${channel} channel: ${error.message}`);
      addLog(`Failed to load ${channel} channel: ${error.message}`, 'error');
    } finally {
      setIsLoadingManifests(false);
    }
  };

  // Handle manifest selection
  const handleManifestSelect = (manifest) => {
    setSelectedManifest(manifest);
    const version = formatVersion(manifest.manifestFirmware);
    addLog(`Selected firmware version: ${version}`, 'info');
  };

  // Connect to device
  const connectDevice = async () => {
    try {
      setIsConnecting(true);

      // Close any existing connection first
      if (transportRef.current) {
        await closeTransport(transportRef.current);
      } else if (portRef.current) {
        try {
          await portRef.current.close();
        } catch (e) {
          // Ignore close errors
        }
      }
      portRef.current = null;
      transportRef.current = null;
      espLoaderRef.current = null;

      addLog('Requesting serial port...', 'info');

      // Request port from user
      const port = await navigator.serial.requestPort({ filters: SERIAL_PORT_FILTERS });
      portRef.current = port;

      // Get port info
      const info = port.getInfo();
      const vidHex = info.usbVendorId?.toString(16).toUpperCase().padStart(4, '0') || 'N/A';
      const pidHex = info.usbProductId?.toString(16).toUpperCase().padStart(4, '0') || 'N/A';
      addLog(`Device: VID=${vidHex}, PID=${pidHex}`, 'info');

      // Small delay to let device settle after port selection
      await new Promise(resolve => setTimeout(resolve, 100));

      addLog('Connecting to ESP32 bootloader...', 'info');
      const device = await connectAndDetect(port, addLog);
      transportRef.current = device.transport;
      espLoaderRef.current = device.loader;

      const chipName = device.chip;
      const macStr = device.mac || 'Unknown';
      const connectedVariant = device.variant;

      setChipInfo({
        chip: chipName,
        mac: macStr,
        variant: connectedVariant
      });

      addLog(`Connected to ${chipName}`, 'success');
      addLog(`MAC Address: ${macStr}`, 'info');
      if (device.psramCap !== null) addLog(`Embedded PSRAM capability: ${device.psramCap}`, 'info');

      // Check if device is supported (a known REKKIE goggle chip + PSRAM combo)
      if (!connectedVariant) {
        addLog(`Warning: ${chipName} is not supported. Only REKKIE Goggles can be flashed.`, 'warning');
        addError(`Unsupported device: ${chipName}. Only REKKIE Goggles are supported for flashing.`);
        trackDeviceUnsupported({ chipType: chipName, deviceMac: macStr });
      } else {
        // Track successful connection to supported device
        addLog(`Detected ${VARIANT_LABELS[connectedVariant] || connectedVariant}`, 'success');
        trackDeviceConnected({ chipType: chipName, deviceMac: macStr });
      }

      setIsConnected(true);

    } catch (error) {
      console.error('Connection error:', error);
      addLog(`Connection failed: ${error.message}`, 'error');
      addError(`Failed to connect: ${error.message}`);

      // connectAndDetect closes the port on its own failures; this covers a
      // failure after requestPort but before it ran.
      if (portRef.current) {
        try {
          await portRef.current.close();
        } catch (e) {
          // Ignore
        }
        portRef.current = null;
      }
      transportRef.current = null;
      espLoaderRef.current = null;
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect device
  const disconnectDevice = async () => {
    const deviceMac = chipInfo?.mac;
    try {
      if (transportRef.current) {
        await closeTransport(transportRef.current);
        addLog('Disconnected from device', 'info');
      } else if (portRef.current) {
        await portRef.current.close();
        addLog('Disconnected from device', 'info');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
    } finally {
      // Track disconnection before clearing state
      if (deviceMac) {
        trackDeviceDisconnected(deviceMac);
      }
      portRef.current = null;
      transportRef.current = null;
      espLoaderRef.current = null;
      setIsConnected(false);
      setChipInfo(null);
    }
  };

  // Start flashing
  const startFlashing = async () => {
    if (!espLoaderRef.current || !selectedManifest) {
      return;
    }
    
    // Check the connected chip maps to a supported REKKIE goggle variant
    if (!variant) {
      addError('Only REKKIE Goggles are supported for flashing');
      addLog(`Error: Device is ${chipInfo?.chip || 'unknown'}, only REKKIE Goggles are supported`, 'error');
      return;
    }

    const version = formatVersion(selectedManifest.manifestFirmware);

    // Log flash started
    trackOTAFlashStarted({
      channel: selectedChannel,
      firmwareVersion: version,
      deviceMac: chipInfo?.mac
    });

    setIsFlashing(true);
    setFlashProgress(0);
    setFlashComplete(false);
    setRebootSent(true);
    setFlashStatus('Downloading firmware from server...');

    try {
      const loader = espLoaderRef.current;

      addLog(`Starting flash for firmware ${version}`, 'info');

      // Download everything before erasing, so a failed download leaves the
      // goggle's current firmware intact.
      const images = await loadFirmwareImages(variant, selectedManifest, addLog);

      await flashImages(loader, images, {
        log: addLog,
        onStage: (stage, imageName) => {
          setFlashStatus(stage === 'erasing' ? 'Erasing flash memory...' : `Flashing ${imageName}...`);
        },
        onProgress: setFlashProgress
      });

      setFlashProgress(100);
      addLog('All firmware files flashed successfully!', 'success');

      // Reboot into the new firmware, as esptool.py's --after hard_reset does.
      // The USB port drops as the goggle restarts, so the session is over.
      setFlashStatus('Restarting goggles...');
      addLog('Restarting goggles into the new firmware...', 'info');
      const rebooted = await rebootIntoApp(loader, transportRef.current);
      portRef.current = null;
      transportRef.current = null;
      espLoaderRef.current = null;
      setRebootSent(rebooted);
      if (rebooted) {
        setFlashStatus('Flash complete! Goggles are restarting.');
        addLog('Goggles restarting. Wait for the display to show a pairing PIN.', 'success');
      } else {
        setFlashStatus('Flash complete! Unplug and reconnect USB-C to restart.');
        addLog('Could not send the restart signal. Unplug and reconnect USB-C to restart the goggles.', 'warning');
      }
      setFlashComplete(true);

      // Log flash completed
      trackOTAFlashCompleted({
        channel: selectedChannel,
        firmwareVersion: version,
        deviceMac: chipInfo?.mac
      });
      
    } catch (error) {
      console.error('Flash error:', error);
      addLog(`Flash error: ${error.message}`, 'error');
      addError(`Flash failed: ${error.message}`);
      setFlashStatus(`Error: ${error.message}`);
      
      // Log flash failed
      trackOTAFlashFailed({
        channel: selectedChannel,
        firmwareVersion: version,
        deviceMac: chipInfo?.mac,
        error: error.message
      });
    } finally {
      setIsFlashing(false);
    }
  };

  const canFlash = isConnected && selectedManifest && !isFlashing && !flashComplete && isSupportedDevice;

  return (
    <div className="ota-management-page">
      <div className="ota-container">
        {/* Header */}
        <div className="ota-header">
          <h1>OTA Firmware Management</h1>
          <p>Flash firmware to REKKIE Goggles from the server</p>
        </div>

        {/* Browser Support Check */}
        {!isSupported && (
          <div className="ota-warning">
            <h3>Use Chrome or Edge to flash firmware</h3>
            <p>Connecting to your device requires the Web Serial API, which is only available in Chromium-based browsers. Open this page in one of the following to continue:</p>
            <ul>
              <li><strong>Google Chrome</strong> (recommended)</li>
              <li><strong>Microsoft Edge</strong></li>
            </ul>
            <p className="ota-warning-note">Safari and Firefox do not support device connections from the web.</p>
          </div>
        )}

        {isSupported && (
          <>
            {/* Connection Section — connect first so we know which goggle variant to load firmware for */}
            <div className="ota-section connection-section">
              <h3>1. Connect Device</h3>
              <p className="section-note">Plug your REKKIE Goggles into USB-C and connect. We'll detect the model and load matching firmware.</p>
              <div className="connection-controls">
                <div className="connection-status">
                  <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}></span>
                  <span className="status-text">
                    {isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                  {chipInfo && (
                    <span className={`chip-info ${!isSupportedDevice ? 'chip-warning' : ''}`}>
                      {isSupportedDevice ? (VARIANT_LABELS[variant] || 'REKKIE Goggles') : chipInfo.chip} • {chipInfo.mac}
                      {!isSupportedDevice && ' (Unsupported)'}
                    </span>
                  )}
                </div>
                <div className="connection-buttons">
                  {!isConnected ? (
                    <Button
                      variant="primary"
                      onClick={connectDevice}
                      disabled={isConnecting}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect Device'}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={disconnectDevice}
                      disabled={isFlashing}
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
              {isConnected && !isSupportedDevice && (
                <div className="chip-error">
                  Only REKKIE Goggles are supported. Please connect your REKKIE Goggles.
                </div>
              )}
            </div>

            {/* Firmware Selection Section — only after a supported device is connected */}
            {isConnected && isSupportedDevice && (
            <div className="ota-section firmware-section">
              <h3>2. Select Firmware</h3>
              <p className="section-note">Choose a firmware channel and version to flash to your {VARIANT_LABELS[variant] || 'REKKIE Goggles'}.</p>
              
              {/* Channel Selection */}
              <div className="channel-selection">
                <label><strong>Channel</strong></label>
                {isLoadingChannels ? (
                  <div className="loading-manifests">Loading channels...</div>
                ) : (
                  <div className="channel-buttons">
                    {availableChannels.map(channel => (
                      <Button
                        key={channel}
                        variant={selectedChannel === channel ? 'primary' : 'outline'}
                        size="small"
                        onClick={() => handleChannelSelect(channel)}
                        disabled={isFlashing || isLoadingManifests}
                      >
                        {formatChannelName(channel)}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Manifest Selection */}
              {selectedChannel && (
                <div className="manifest-selection">
                  <label><strong>Firmware Version</strong></label>
                  {isLoadingManifests ? (
                    <div className="loading-manifests">Loading versions...</div>
                  ) : manifests.length === 0 ? (
                    <div className="no-manifests">No firmware versions available in this channel.</div>
                  ) : (
                    <div className="manifest-list">
                      {/* Latest version (first in list) */}
                      {manifests.length > 0 && (
                        <div
                          className={`manifest-item latest ${selectedManifest === manifests[0] ? 'selected' : ''}`}
                          onClick={() => !isFlashing && handleManifestSelect(manifests[0])}
                        >
                          <span className="manifest-version">
                            {formatVersion(manifests[0].manifestFirmware)}
                            <span className="latest-badge">Latest</span>
                          </span>
                          {selectedManifest === manifests[0] && <span className="manifest-check">✓</span>}
                        </div>
                      )}
                      
                      {/* Other versions toggle */}
                      {manifests.length > 1 && (
                        <>
                          <button
                            className="other-versions-toggle"
                            onClick={() => setShowOtherVersions(!showOtherVersions)}
                            disabled={isFlashing}
                          >
                            <span className={`toggle-arrow ${showOtherVersions ? 'open' : ''}`}>▶</span>
                            Other versions ({manifests.length - 1})
                          </button>
                          
                          {showOtherVersions && (
                            <div className="other-versions-list">
                              {manifests.slice(1).map((manifest, index) => {
                                const version = formatVersion(manifest.manifestFirmware);
                                const isSelected = selectedManifest === manifest;
                                return (
                                  <div
                                    key={index + 1}
                                    className={`manifest-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => !isFlashing && handleManifestSelect(manifest)}
                                  >
                                    <span className="manifest-version">{version}</span>
                                    {isSelected && <span className="manifest-check">✓</span>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Changelog for selected version */}
                  {selectedManifest && selectedManifest.changelog && (
                    <div className="changelog-section">
                      <label><strong>Changelog</strong></label>
                      <div className="changelog-content">
                        {selectedManifest.changelog}
                      </div>
                    </div>
                  )}
                  
                  {/* Developer Notes - only visible to @rekkie.com users */}
                  {selectedManifest && selectedManifest.developerNotes && (
                    <div className="developer-notes-section">
                      <label><strong>Developer Notes (Visible to you as a REKKIE employee)</strong></label>
                      <div className="developer-notes-content">
                        {selectedManifest.developerNotes}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Flash Controls Section - Only show when connected to a supported REKKIE Goggle */}
            {selectedManifest && isConnected && isSupportedDevice && (
              <div className="ota-section flash-section">
                <h3>3. Flash Firmware</h3>
                
                <div className="flash-controls">
                  <div className="flash-info">
                    {flashStatus && (
                      <div className={`flash-status ${flashComplete ? 'complete' : ''}`}>
                        {flashComplete && <span className="status-icon">✓</span>}
                        {flashStatus}
                      </div>
                    )}
                    
                    {(isFlashing || flashProgress > 0) && (
                      <div className="flash-progress">
                        <div className="progress-bar">
                          <div 
                            className={`progress-fill ${flashComplete ? 'complete' : ''}`}
                            style={{ width: `${flashProgress}%` }}
                          />
                        </div>
                        <span className="progress-text">{Math.round(flashProgress)}%</span>
                      </div>
                    )}
                    
                    {flashComplete && (
                      <div className="flash-complete-message">
                        <ul>
                          {rebootSent ? (
                            <li>Goggles are restarting. Wait for the display to turn on and show a new pairing PIN</li>
                          ) : (
                            <>
                              <li>Disconnect goggles (unplug USB-C cable)</li>
                              <li>Reconnect goggles (plug USB-C cable back in)</li>
                              <li>Wait for the display to turn on and show a new pairing PIN</li>
                            </>
                          )}
                          <li>You are now ready to disconnect the goggles over USB-C</li>
                          <li><strong>Note:</strong> If your goggles were previously paired, you will need to forget goggles in the REKKIE app and re-pair them</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flash-actions">
                    <Button
                      variant="primary"
                      size="large"
                      onClick={startFlashing}
                      disabled={!canFlash}
                    >
                      {isFlashing ? 'Flashing...' : `Flash ${formatVersion(selectedManifest.manifestFirmware)}`}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Logs Section */}
            <div className="ota-section logs-section">
              <div className="logs-header">
                <h3>Flash Log</h3>
                <div className="logs-actions">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={clearLogs}
                    disabled={flashLogs.length === 0}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={exportLogs}
                    disabled={flashLogs.length === 0}
                  >
                    Export
                  </Button>
                </div>
              </div>
              
              <div className="logs-container">
                {flashLogs.length === 0 ? (
                  <div className="logs-empty">
                    Logs will appear here during flashing...
                  </div>
                ) : (
                  flashLogs.map(log => (
                    <div key={log.id} className={`log-entry log-${log.type}`}>
                      <span className="log-timestamp">[{log.timestamp}]</span>
                      <span className="log-message">{log.message}</span>
                    </div>
                  ))
                )}
                <div ref={logsEndRef} />
              </div>
            </div>

            {/* Seed Firmware Section - Only for @rekkie.com users */}
            {isRekkieUser && (
              <div className="ota-section seed-section">
                <div className="seed-header" onClick={() => setShowSeedForm(!showSeedForm)} style={{ cursor: 'pointer' }}>
                  <h3>
                    <span className={`toggle-arrow ${showSeedForm ? 'open' : ''}`}>&#9654;</span>
                    {' '}Seed Firmware
                  </h3>
                  <p className="section-note">Upload a new firmware build to a channel</p>
                </div>

                {showSeedForm && (
                  <div className="seed-form">
                    {/* File Inputs */}
                    <div className="seed-field">
                      <label>Firmware Binary (.bin) <span className="seed-required">*</span></label>
                      <div className="seed-file-input">
                        <input
                          type="file"
                          accept=".bin"
                          onChange={(e) => setSeedBinFile(e.target.files[0] || null)}
                          disabled={isSeeding}
                        />
                        {seedBinFile && <span className="seed-file-name">{seedBinFile.name} ({formatFileSize(seedBinFile.size)})</span>}
                      </div>
                    </div>

                    <div className="seed-field">
                      <label>ELF File (.elf) <span className="seed-required">*</span></label>
                      <div className="seed-file-input">
                        <input
                          type="file"
                          accept=".elf"
                          onChange={(e) => setSeedElfFile(e.target.files[0] || null)}
                          disabled={isSeeding}
                        />
                        {seedElfFile && <span className="seed-file-name">{seedElfFile.name} ({formatFileSize(seedElfFile.size)})</span>}
                      </div>
                    </div>

                    {/* Build Number */}
                    <div className="seed-field">
                      <label>Build Number <span className="seed-required">*</span></label>
                      <div className="seed-version-inputs">
                        <div className="seed-version-field">
                          <span className="seed-version-label">Major</span>
                          <input
                            type="number"
                            min="0"
                            value={seedMajor}
                            onChange={(e) => setSeedMajor(e.target.value)}
                            placeholder="0"
                            disabled={isSeeding}
                          />
                        </div>
                        <span className="seed-version-dot">.</span>
                        <div className="seed-version-field">
                          <span className="seed-version-label">Minor</span>
                          <input
                            type="number"
                            min="0"
                            value={seedMinor}
                            onChange={(e) => setSeedMinor(e.target.value)}
                            placeholder="0"
                            disabled={isSeeding}
                          />
                        </div>
                        <span className="seed-version-dot">.</span>
                        <div className="seed-version-field">
                          <span className="seed-version-label">Build</span>
                          <input
                            type="number"
                            min="0"
                            value={seedBuild}
                            onChange={(e) => setSeedBuild(e.target.value)}
                            placeholder="0"
                            disabled={isSeeding}
                          />
                        </div>
                        <span className="seed-version-dot">.</span>
                        <div className="seed-version-field">
                          <span className="seed-version-label">Patch</span>
                          <input
                            type="number"
                            min="0"
                            value={seedPatch}
                            onChange={(e) => setSeedPatch(e.target.value)}
                            placeholder="0"
                            disabled={isSeeding}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Hardware Variant Picker */}
                    <div className="seed-field">
                      <label>Hardware Variant <span className="seed-required">*</span></label>
                      <div className="seed-channel-buttons">
                        {SEEDABLE_VARIANTS.map(v => (
                          <button
                            key={v}
                            className={`seed-channel-btn ${seedVariant === v ? 'selected' : ''}`}
                            onClick={() => setSeedVariant(v)}
                            disabled={isSeeding}
                          >
                            {VARIANT_LABELS[v] || v}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Channel Picker */}
                    <div className="seed-field">
                      <label>Channel <span className="seed-required">*</span></label>
                      <div className="seed-channel-buttons">
                        {seedableChannels.map(ch => (
                          <button
                            key={ch}
                            className={`seed-channel-btn ${seedChannel === ch ? 'selected' : ''}`}
                            onClick={() => setSeedChannel(ch)}
                            disabled={isSeeding}
                          >
                            {ch}
                          </button>
                        ))}
                        {seedableChannels.length === 0 && (
                          <span className="section-note">Loading channels...</span>
                        )}
                      </div>
                    </div>

                    {/* Developer Notes */}
                    <div className="seed-field">
                      <label>Developer Notes <span className="seed-required">*</span></label>
                      <textarea
                        className="seed-textarea"
                        value={seedDevNotes}
                        onChange={(e) => setSeedDevNotes(e.target.value)}
                        placeholder="Internal notes (only visible to @rekkie.com users)"
                        rows={3}
                        disabled={isSeeding}
                      />
                    </div>

                    {/* User-Facing Changelog */}
                    <div className="seed-field">
                      <label>User-Facing Changelog <span className="seed-required">*</span></label>
                      <textarea
                        className="seed-textarea"
                        value={seedChangelog}
                        onChange={(e) => setSeedChangelog(e.target.value)}
                        placeholder="Changelog visible to all users when selecting this firmware version"
                        rows={3}
                        disabled={isSeeding}
                      />
                    </div>

                    {/* Result message */}
                    {seedResult && (
                      <div className={`seed-result ${seedResult.success ? 'seed-success' : 'seed-error'}`}>
                        {seedResult.success
                          ? `Firmware ${seedResult.version} seeded to ${seedResult.channel} successfully.`
                          : `Failed: ${seedResult.message}`}
                      </div>
                    )}

                    {/* Submit */}
                    <div className="seed-actions">
                      <Button
                        variant="primary"
                        onClick={handleSeedFirmware}
                        disabled={!isSeedFormValid || isSeeding}
                      >
                        {isSeeding ? 'Seeding...' : 'Seed Firmware'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OTAManagementPage;
